import React from 'react'
import { useConfiguration } from '../contexts/ConfigurationContext'
import { DollarSign, TrendingUp, Calculator } from 'lucide-react'

function CostDisplay() {
  const { state } = useConfiguration()

  if (!state.selectedProvider) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <DollarSign className="h-4 w-4 text-primary-600" />
          <h3 className="text-base font-semibold text-gray-900">
            Cost Estimation
          </h3>
        </div>
        <div className="text-center py-6 text-gray-500">
          <Calculator className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Select a provider to see cost estimates</p>
        </div>
      </div>
    )
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount)
  }

  const calculateYearlyCost = () => {
    return state.costData ? state.costData.monthly * 12 : 0
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-4">
        <DollarSign className="h-4 w-4 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Cost Estimation
        </h3>
      </div>

      {state.costData ? (
        <div className="space-y-4">
          {/* Main Cost Display */}
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">
              {formatCurrency(state.costData.monthly)}
            </div>
            <div className="text-sm text-gray-500">per month</div>
          </div>

          {/* Cost Breakdown */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-medium text-gray-900">
                {formatCurrency(state.costData.hourly)}
              </div>
              <div className="text-gray-500">Hourly</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-medium text-gray-900">
                {formatCurrency(calculateYearlyCost())}
              </div>
              <div className="text-gray-500">Yearly</div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Cost Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">vCPUs ({state.vcpus}x)</span>
                <span className="font-medium">
                  {formatCurrency(state.vcpus * state.selectedProvider.pricing.vcpu * 730)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">RAM ({state.ramGb}GB)</span>
                <span className="font-medium">
                  {formatCurrency(state.ramGb * state.selectedProvider.pricing.ramGb * 730)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Storage ({state.storageGb}GB)</span>
                <span className="font-medium">
                  {formatCurrency(state.storageGb * state.selectedProvider.pricing.storageGb * 730)}
                </span>
              </div>
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Total Monthly</span>
                <span>{formatCurrency(state.costData.monthly)}</span>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Pricing Information</p>
                <ul className="space-y-1 text-xs">
                  <li>• Pay-as-you-go pricing model</li>
                  <li>• No upfront costs or commitments</li>
                  <li>• Prices shown are estimates in USD</li>
                  <li>• Based on {state.selectedProvider.shortName} standard rates</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comparison Note */}
          <div className="text-xs text-gray-500 text-center">
            Costs calculated for {state.selectedProvider.name} •
            730 hours/month average
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Calculating costs...</p>
        </div>
      )}
    </div>
  )
}

export default CostDisplay 